/**
 * 減壹加活動助理 - 共用註冊/建立表單
 * 標題、商店名稱、類型（選填）、建立按鈕統一，供 Group 註冊與 Square 新建共用
 */

import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import { StorefrontIcon } from '@/components/StorefrontIcon';

export const TYPE_OPTIONS = [
  { value: '商品', label: '商品（團購、烘焙等）' },
  { value: '席次', label: '席次（球隊、活動等）' },
  { value: '席次(羽球)', label: '席次(羽球)' },
];

export interface LiteActivityAssistantFormValue {
  name: string;
  type: string;
  customType: string;
}

export interface LiteActivityAssistantFormProps {
  value: LiteActivityAssistantFormValue;
  onChange: (value: LiteActivityAssistantFormValue) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean;
  error: string | null;
  /** 選填：次要按鈕（如「返回」） */
  secondaryButton?: {
    label: string;
    onClick: () => void;
  };
}

export function LiteActivityAssistantForm({
  value,
  onChange,
  onSubmit,
  submitting,
  error,
  secondaryButton,
}: LiteActivityAssistantFormProps) {
  const [typeDrawerOpen, setTypeDrawerOpen] = useState(false);

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="mb-4">
            <StorefrontIcon className="w-16 h-16" withBorder />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            歡迎使用 <span className="font-semibold text-blue-600">減壹加</span>{' '}
            活動助理
          </h1>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              商店名稱 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={value.name}
              onChange={e => onChange({ ...value, name: e.target.value })}
              placeholder="例如：我的商店"
              className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              maxLength={255}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              類型 <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setTypeDrawerOpen(true)}
              className="w-full p-3 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 text-left flex items-center justify-between"
            >
              <span className={!value.type ? 'text-gray-500' : ''}>
                {value.type || '請選擇類型'}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="shrink-0 text-gray-400"
                aria-hidden
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <Drawer open={typeDrawerOpen} onOpenChange={setTypeDrawerOpen}>
              <DrawerContent>
                <DrawerHeader>
                  <DrawerTitle>類型</DrawerTitle>
                  <DrawerDescription>請選擇類型（必填）</DrawerDescription>
                </DrawerHeader>
                <div className="px-4 pb-4 space-y-1 max-h-[60vh] overflow-y-auto">
                  {TYPE_OPTIONS.map(opt => (
                    <button
                      key={opt.value || 'empty'}
                      type="button"
                      onClick={() => {
                        onChange({
                          ...value,
                          type: opt.value,
                          customType: '',
                        });
                        setTypeDrawerOpen(false);
                      }}
                      className="w-full p-4 text-left rounded-lg hover:bg-gray-100 font-medium"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </DrawerContent>
            </Drawer>
          </div>

          <div className="mt-6 flex gap-3">
            {secondaryButton && (
              <button
                type="button"
                onClick={secondaryButton.onClick}
                className="flex-1 rounded-lg border-2 border-gray-300 text-gray-700 py-3 hover:bg-gray-50 font-medium"
              >
                {secondaryButton.label}
              </button>
            )}
            <button
              type="submit"
              disabled={submitting || !value.name.trim() || !value.type}
              className={
                secondaryButton
                  ? 'flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed'
                  : 'w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed'
              }
            >
              {submitting ? '建立中…' : '建立'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
